import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  where
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaEye, FaExclamationTriangle, FaUser, FaCalendar, FaVenusMars, FaGraduationCap, FaHeart, FaTarget, FaImage, FaChalkboardTeacher, FaClock, FaEye as FaEyeIcon, FaFileAlt, FaCrown, FaHourglassHalf } from 'react-icons/fa';
import { formatDate } from '../../utils/dateUtils';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterSubscription, setFilterSubscription] = useState('all');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    // Firebase Auth user data
    id: '',
    email: '',
    displayName: '',
    photoURL: '',
    emailVerified: false,
    phoneNumber: '',
    lastLoginAt: '',
    providerData: [],
    multiFactor: null,
    createdAt: '',
    updatedAt: '',
    
    // Profile data
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    role: '',
    interests: [],
    goals: [],
    picture: '',
    teacherId: '',
    averageLearningSessionTime: 0,
    averageAttentionPercentage: 0,
    allLearningSessionsSummary: '',
    subscriptionStatus: 'free',
    usageTime: 0,
    totalMinutes: 10
  });

  // Fetch users from Firestore
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch users from users collection
      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, orderBy('createdAt', 'desc'));
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch profiles from profiles collection
      const profilesRef = collection(db, 'profiles');
      const profilesSnapshot = await getDocs(profilesRef);
      const profilesData = profilesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Create a map of profiles by userId for quick lookup
      const profilesMap = {};
      profilesData.forEach(profile => {
        profilesMap[profile.userId] = profile;
      });

      // Merge users with their profiles
      const mergedUsers = usersData.map(user => {
        const profile = profilesMap[user.id] || {};
        return {
          ...user,
          // Profile data
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          dateOfBirth: profile.dateOfBirth || '',
          gender: profile.gender || '',
          role: profile.role || '',
          interests: profile.interests || [],
          goals: profile.goals || [],
          picture: profile.picture || user.photoURL || '',
          teacherId: profile.teacherId || '',
          averageLearningSessionTime: profile.averageLearningSessionTime || 0,
          averageAttentionPercentage: profile.averageAttentionPercentage || 0,
          allLearningSessionsSummary: profile.allLearningSessionsSummary || '',
          subscriptionStatus: profile.subscriptionStatus || 'free',
          usageTime: profile.usageTime || 0,
          totalMinutes: profile.totalMinutes || 10
        };
      });

      setUsers(mergedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.code === 'permission-denied') {
        setError('Permission denied. Please check your Firestore security rules or contact your administrator.');
      } else if (error.code === 'unavailable') {
        setError('Firestore is currently unavailable. Please check your internet connection and try again.');
      } else {
        setError(`Error loading users: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle array inputs (interests, goals)
  const handleArrayInput = (field, value) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({
      ...prev,
      [field]: items
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      // Firebase Auth user data
      id: '',
      email: '',
      displayName: '',
      photoURL: '',
      emailVerified: false,
      phoneNumber: '',
      lastLoginAt: '',
      providerData: [],
      multiFactor: null,
      createdAt: '',
      updatedAt: '',
      
      // Profile data
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      role: '',
      interests: [],
      goals: [],
      picture: '',
      teacherId: '',
      averageLearningSessionTime: 0,
      averageAttentionPercentage: 0,
      allLearningSessionsSummary: '',
      subscriptionStatus: 'free',
      usageTime: 0,
      totalMinutes: 10
    });
    setEditingUser(null);
  };

  // Open modal for creating new user
  const handleCreateUser = () => {
    resetForm();
    setShowModal(true);
  };

  // Open modal for editing user
  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      // Firebase Auth user data
      id: user.id || '',
      email: user.email || '',
      displayName: user.displayName || '',
      photoURL: user.photoURL || '',
      emailVerified: user.emailVerified || false,
      phoneNumber: user.phoneNumber || '',
      lastLoginAt: user.lastLoginAt || '',
      providerData: user.providerData || [],
      multiFactor: user.multiFactor || null,
      createdAt: user.createdAt || '',
      updatedAt: user.updatedAt || '',
      
      // Profile data (from merged data)
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      dateOfBirth: user.dateOfBirth || '',
      gender: user.gender || '',
      role: user.role || '',
      interests: user.interests || [],
      goals: user.goals || [],
      picture: user.picture || user.photoURL || '',
      teacherId: user.teacherId || '',
      averageLearningSessionTime: user.averageLearningSessionTime || 0,
      averageAttentionPercentage: user.averageAttentionPercentage || 0,
      allLearningSessionsSummary: user.allLearningSessionsSummary || '',
      subscriptionStatus: user.subscriptionStatus || 'free',
      usageTime: user.usageTime || 0,
      totalMinutes: user.totalMinutes || 10
    });
    setShowModal(true);
  };

  // Save user (create or update)
  const handleSaveUser = async (e) => {
    e.preventDefault();
    try {
      setError('');
      
      // Prepare user data (Firebase Auth data)
      const userData = {
        email: formData.email,
        displayName: formData.displayName,
        photoURL: formData.photoURL,
        emailVerified: formData.emailVerified,
        phoneNumber: formData.phoneNumber,
        lastLoginAt: formData.lastLoginAt,
        providerData: formData.providerData,
        multiFactor: formData.multiFactor,
        updatedAt: new Date().toISOString()
      };

      // Prepare profile data
      const profileData = {
        userId: formData.id || editingUser?.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        role: formData.role,
        interests: formData.interests,
        goals: formData.goals,
        picture: formData.picture,
        teacherId: formData.teacherId,
        averageLearningSessionTime: parseFloat(formData.averageLearningSessionTime) || 0,
        averageAttentionPercentage: parseFloat(formData.averageAttentionPercentage) || 0,
        allLearningSessionsSummary: formData.allLearningSessionsSummary,
        subscriptionStatus: formData.subscriptionStatus,
        usageTime: parseFloat(formData.usageTime) || 0,
        totalMinutes: parseFloat(formData.totalMinutes) || 10,
        updatedAt: new Date().toISOString()
      };

      if (editingUser) {
        // Update existing user
        const userRef = doc(db, 'users', editingUser.id);
        await updateDoc(userRef, userData);

        // Check if profile exists, if not create it
        const profilesRef = collection(db, 'profiles');
        const profileQuery = query(profilesRef, where('userId', '==', editingUser.id));
        const profileSnapshot = await getDocs(profileQuery);
        
        if (profileSnapshot.empty) {
          // Create new profile
          await addDoc(collection(db, 'profiles'), {
            ...profileData,
            createdAt: new Date().toISOString()
          });
        } else {
          // Update existing profile
          const profileDoc = profileSnapshot.docs[0];
          await updateDoc(doc(db, 'profiles', profileDoc.id), profileData);
        }
      } else {
        // Create new user
        const newUserRef = await addDoc(collection(db, 'users'), {
          ...userData,
          createdAt: new Date().toISOString()
        });

        // Create new profile with the new user ID
        await addDoc(collection(db, 'profiles'), {
          ...profileData,
          userId: newUserRef.id,
          createdAt: new Date().toISOString()
        });
      }
      
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.code === 'permission-denied') {
        setError('Permission denied. You do not have permission to save users.');
      } else {
        setError(`Error saving user: ${error.message}`);
      }
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This will also delete their profile.')) {
      try {
        setError('');
        
        // Delete user from users collection
        await deleteDoc(doc(db, 'users', userId));
        
        // Find and delete profile from profiles collection
        const profilesRef = collection(db, 'profiles');
        const profileQuery = query(profilesRef, where('userId', '==', userId));
        const profileSnapshot = await getDocs(profileQuery);
        
        if (!profileSnapshot.empty) {
          const profileDoc = profileSnapshot.docs[0];
          await deleteDoc(doc(db, 'profiles', profileDoc.id));
        }
        
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        if (error.code === 'permission-denied') {
          setError('Permission denied. You do not have permission to delete users.');
        } else {
          setError(`Error deleting user: ${error.message}`);
        }
      }
    }
  };

  // Filter users based on search term, role, and subscription
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesSubscription = filterSubscription === 'all' || user.subscriptionStatus === filterSubscription;
    
    return matchesSearch && matchesRole && matchesSubscription;
  });

  // Get subscription badge color
  const getSubscriptionBadgeColor = (status) => {
    switch (status) {
      case 'premium':
        return 'bg-purple-100 text-purple-800';
      case 'pro':
        return 'bg-blue-100 text-blue-800';
      case 'free':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'teacher':
        return 'bg-blue-100 text-blue-800';
      case 'student':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format time from minutes
  const formatTime = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage all users in the system including students, teachers, and administrators.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={handleCreateUser}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
          >
            <FaPlus className="mr-2 h-4 w-4" />
            Add User
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <button
                    onClick={() => setError('')}
                    className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Role Filter */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="all">All Roles</option>
            <option value="student">Students</option>
            <option value="teacher">Teachers</option>
            <option value="admin">Admins</option>
          </select>

          {/* Subscription Filter */}
          <select
            value={filterSubscription}
            onChange={(e) => setFilterSubscription(e.target.value)}
            className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          >
            <option value="all">All Subscriptions</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="premium">Premium</option>
          </select>
        </div>

        <div className="text-sm text-gray-700">
          {filteredUsers.length} of {users.length} users
        </div>
      </div>

      {/* Users Table */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <FaExclamationTriangle className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Unable to load users</h3>
                  <p className="mt-1 text-sm text-gray-500">{error}</p>
                  <div className="mt-6">
                    <button
                      onClick={fetchUsers}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role & Subscription
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Learning Stats
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {user.picture || user.photoURL ? (
                                <img 
                                  className="h-10 w-10 rounded-full object-cover" 
                                  src={user.picture || user.photoURL} 
                                  alt={user.displayName || `${user.firstName} ${user.lastName}`}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                  <FaUser className="h-5 w-5 text-gray-600" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.firstName && user.lastName 
                                  ? `${user.firstName} ${user.lastName}`
                                  : user.displayName || user.email
                                }
                              </div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                              {user.gender && (
                                <div className="text-xs text-gray-400 flex items-center">
                                  <FaVenusMars className="h-3 w-3 mr-1" />
                                  {user.gender}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            {user.role && (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                                {user.role}
                              </span>
                            )}
                            <div>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSubscriptionBadgeColor(user.subscriptionStatus)}`}>
                                {user.subscriptionStatus}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center">
                              <FaClock className="h-4 w-4 text-gray-400 mr-1" />
                              {formatTime(user.averageLearningSessionTime || 0)}
                            </div>
                            <div className="flex items-center mt-1">
                              <FaEyeIcon className="h-4 w-4 text-gray-400 mr-1" />
                              {user.averageAttentionPercentage?.toFixed(1) || 0}% attention
                            </div>
                            <div className="flex items-center mt-1">
                              <FaHourglassHalf className="h-4 w-4 text-gray-400 mr-1" />
                              {formatTime(user.usageTime || 0)} / {formatTime(user.totalMinutes || 10)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.phoneNumber || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.createdAt, 'short')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <FaEdit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {!loading && !error && filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <FaUser className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by creating a new user.</p>
                  <div className="mt-6">
                    <button
                      onClick={handleCreateUser}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <FaPlus className="mr-2 h-4 w-4" />
                      Add User
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h3>
              <form onSubmit={handleSaveUser} className="space-y-4">
                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Picture URL</label>
                  <input
                    type="url"
                    name="picture"
                    value={formData.picture}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select role</option>
                      <option value="student">Student</option>
                      <option value="teacher">Teacher</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Subscription</label>
                    <select
                      name="subscriptionStatus"
                      value={formData.subscriptionStatus}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Teacher ID</label>
                  <input
                    type="text"
                    name="teacherId"
                    value={formData.teacherId}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Interests (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.interests.join(', ')}
                    onChange={(e) => handleArrayInput('interests', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Math, Science, English"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Goals (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.goals.join(', ')}
                    onChange={(e) => handleArrayInput('goals', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Learn Python, Improve Math"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Avg Session Time (min)</label>
                    <input
                      type="number"
                      name="averageLearningSessionTime"
                      value={formData.averageLearningSessionTime}
                      onChange={handleInputChange}
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Avg Attention (%)</label>
                    <input
                      type="number"
                      name="averageAttentionPercentage"
                      value={formData.averageAttentionPercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      step="0.1"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Usage Time (min)</label>
                    <input
                      type="number"
                      name="usageTime"
                      value={formData.usageTime}
                      onChange={handleInputChange}
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Total Minutes</label>
                    <input
                      type="number"
                      name="totalMinutes"
                      value={formData.totalMinutes}
                      onChange={handleInputChange}
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Learning Sessions Summary</label>
                  <textarea
                    name="allLearningSessionsSummary"
                    value={formData.allLearningSessionsSummary}
                    onChange={handleInputChange}
                    rows="3"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {editingUser ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 